/**
 * v3.18-1283 — PAYMENT-008 Unified Funding Sources block.
 *
 * Replaces the historical Credit Card / ACH Confirmation blocks. One
 * card per payment applied to the deal, unlimited count, responsive
 * 2-column grid on desktop, 1-column on mobile.
 *
 * Canonical data lives in `client.funding_sources[]` on the backend
 * (see backend/routes/funding_sources.py). Legacy flat fields are
 * compatibility mirrors only — this component NEVER reads them.
 *
 * Every mutation goes through the funding-sources API so the audit
 * trail, mirror write-through, and totals recomputation stay authoritative.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Landmark, Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const METHOD_CC  = 'credit_card';
const METHOD_ACH = 'ach';

const CC_STATUSES  = ['Paid', 'Pending', 'Failed'];
const ACH_STATUSES = ['Paid', 'Pending', 'Failed'];

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Small labelled field used inside every funding source card ──
const FundingField = ({
  label,
  value,
  onCommit,
  type = 'text',
  placeholder = '',
  testid,
}) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = () => {
    const next = type === 'number'
      ? (draft === '' || draft === null ? null : Number(draft))
      : (draft === '' ? null : draft);
    if (next === value) return;
    onCommit(next);
  };
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className="text-[10px] text-slate-500 uppercase tracking-wide flex-shrink-0 w-40">
        {label}
      </span>
      <Input
        type={type}
        value={draft ?? ''}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="h-7 text-xs flex-1 min-w-0"
        data-testid={testid}
      />
    </div>
  );
};

// ── One funding source card ──
const FundingSourceCard = ({
  source,
  index,
  disabled,
  saving,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const method  = source.method === METHOD_CC ? METHOD_CC : METHOD_ACH;
  const payment = source.payment_info || {};
  const wire    = source.wire_info    || {};

  const setMethod = (v) => onPatch({ method: v });
  const patchPayment = (partial) => onPatch({ payment_info: { ...partial } });
  const patchWire    = (partial) => onPatch({ wire_info:    { ...partial } });

  const Icon = method === METHOD_CC ? CreditCard : Landmark;

  return (
    <Card
      className="rounded-md border border-slate-200 bg-white shadow-sm"
      data-testid={`funding-source-card-${index}`}
      data-source-id={source.id}
      data-method={method}
    >
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <span
              className="text-xs font-semibold text-slate-700 uppercase tracking-wide"
              data-testid={`funding-source-title-${index}`}
            >
              Funding Source {index + 1}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {canMoveUp && (
              <button
                type="button"
                onClick={onMoveUp}
                title="Move up"
                className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
                data-testid={`funding-source-move-up-${index}`}
              >
                <GripVertical className="h-3 w-3 -rotate-90" />
              </button>
            )}
            {canMoveDown && (
              <button
                type="button"
                onClick={onMoveDown}
                title="Move down"
                className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
                data-testid={`funding-source-move-down-${index}`}
              >
                <GripVertical className="h-3 w-3 rotate-90" />
              </button>
            )}
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled || saving}
              className="h-6 w-6 rounded hover:bg-red-50 flex items-center justify-center text-slate-400 hover:text-red-600 disabled:opacity-40"
              title="Remove funding source"
              data-testid={`funding-source-remove-${index}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide w-40">
            Payment Method
          </span>
          <Select value={method} onValueChange={setMethod} disabled={disabled}>
            <SelectTrigger
              className="h-7 text-xs flex-1"
              data-testid={`funding-source-method-select-${index}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={METHOD_CC}  className="text-xs">Credit Card</SelectItem>
              <SelectItem value={METHOD_ACH} className="text-xs">ACH</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="pt-0 px-3 pb-3">
        {/* Payment Information */}
        <div className="mt-1 mb-2">
          <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">
            Payment Information
          </p>
          {method === METHOD_CC ? (
            <div className="space-y-0.5">
              <FundingField
                label="Payment Delivery Date"
                value={payment.cc_payment_delivery_date}
                type="date"
                onCommit={(v) => patchPayment({ cc_payment_delivery_date: v })}
                testid={`fs-${index}-cc-delivery-date`}
              />
              <div className="flex items-center gap-2 py-0.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wide w-40">Pay From</span>
                <span className="text-xs text-slate-700 font-semibold flex-1">Card</span>
              </div>
              <div className="flex items-center gap-2 py-0.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wide w-40">Status</span>
                <Select
                  value={payment.cc_status || ''}
                  onValueChange={(v) => patchPayment({ cc_status: v })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-7 text-xs flex-1" data-testid={`fs-${index}-cc-status`}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {CC_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <FundingField
                label="Reference Number"
                value={payment.cc_reference_number}
                onCommit={(v) => patchPayment({ cc_reference_number: v })}
                testid={`fs-${index}-cc-reference`}
              />
              <FundingField
                label="Total Invoiced Amount"
                value={payment.cc_total_invoiced_amount}
                type="number"
                onCommit={(v) => patchPayment({ cc_total_invoiced_amount: v })}
                testid={`fs-${index}-cc-invoiced`}
              />
              <FundingField
                label="Convenience Fee"
                value={payment.cc_convenience_fee}
                type="number"
                onCommit={(v) => patchPayment({ cc_convenience_fee: v })}
                testid={`fs-${index}-cc-convenience`}
              />
              <FundingField
                label="Total Payment"
                value={payment.cc_total_payment}
                type="number"
                onCommit={(v) => patchPayment({ cc_total_payment: v })}
                testid={`fs-${index}-cc-payment`}
              />
              <FundingField
                label="Card Transaction Fee (%)"
                value={payment.cc_card_transaction_fee_pct}
                type="number"
                onCommit={(v) => patchPayment({ cc_card_transaction_fee_pct: v })}
                testid={`fs-${index}-cc-tx-fee-pct`}
              />
              <FundingField
                label="Deposit Amount"
                value={payment.cc_deposit_amount}
                type="number"
                onCommit={(v) => patchPayment({ cc_deposit_amount: v })}
                testid={`fs-${index}-cc-deposit`}
              />
            </div>
          ) : (
            <div className="space-y-0.5">
              <FundingField
                label="ACH Signed Confirm #"
                value={payment.ach_signature_confirmation}
                onCommit={(v) => patchPayment({ ach_signature_confirmation: v })}
                testid={`fs-${index}-ach-signed-confirm`}
              />
              <FundingField
                label="Approved ACH Amount"
                value={payment.ach_approved_amount}
                type="number"
                onCommit={(v) => patchPayment({ ach_approved_amount: v })}
                testid={`fs-${index}-ach-approved`}
              />
              <FundingField
                label="ACH Transfer #"
                value={payment.ach_bank_confirmation_number}
                onCommit={(v) => patchPayment({ ach_bank_confirmation_number: v })}
                testid={`fs-${index}-ach-transfer-num`}
              />
              <FundingField
                label="ACH Transfer Date"
                value={payment.ach_transit_date}
                type="date"
                onCommit={(v) => patchPayment({ ach_transit_date: v })}
                testid={`fs-${index}-ach-transit-date`}
              />
              <div className="flex items-center gap-2 py-0.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wide w-40">Status</span>
                <Select
                  value={payment.ach_status || ''}
                  onValueChange={(v) => patchPayment({ ach_status: v })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-7 text-xs flex-1" data-testid={`fs-${index}-ach-status`}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACH_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <FundingField
                label="Reference Number"
                value={payment.ach_reference_number}
                onCommit={(v) => patchPayment({ ach_reference_number: v })}
                testid={`fs-${index}-ach-reference`}
              />
              <FundingField
                label="Deposit Amount"
                value={payment.ach_deposit_amount}
                type="number"
                onCommit={(v) => patchPayment({ ach_deposit_amount: v })}
                testid={`fs-${index}-ach-deposit`}
              />
            </div>
          )}
        </div>

        {/* Wire Transfer Information — always per source */}
        <div className="mt-2 pt-2 border-t border-slate-200">
          <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">
            Wire Transfer Information
          </p>
          <div className="space-y-0.5">
            <FundingField
              label="Wire Reference #"
              value={wire.wire_reference_number}
              onCommit={(v) => patchWire({ wire_reference_number: v })}
              testid={`fs-${index}-wire-reference`}
            />
            <FundingField
              label="Date Sent"
              value={wire.wire_date_sent}
              type="date"
              onCommit={(v) => patchWire({ wire_date_sent: v })}
              testid={`fs-${index}-wire-date-sent`}
            />
            <FundingField
              label="Amount"
              value={wire.wire_amount}
              type="number"
              onCommit={(v) => patchWire({ wire_amount: v })}
              testid={`fs-${index}-wire-amount`}
            />
            <FundingField
              label="Deposit Date"
              value={wire.wire_deposit_date}
              type="date"
              onCommit={(v) => patchWire({ wire_deposit_date: v })}
              testid={`fs-${index}-wire-deposit-date`}
            />
            <FundingField
              label="Wire Transfer Fee"
              value={wire.wire_transfer_fee}
              type="number"
              onCommit={(v) => patchWire({ wire_transfer_fee: v })}
              testid={`fs-${index}-wire-fee`}
            />
            <FundingField
              label="Paid to PTC"
              value={wire.wire_paid_to_ptc}
              type="number"
              onCommit={(v) => patchWire({ wire_paid_to_ptc: v })}
              testid={`fs-${index}-wire-paid-ptc`}
            />
            <FundingField
              label="Paid to Partner"
              value={wire.wire_paid_to_partner}
              type="number"
              onCommit={(v) => patchWire({ wire_paid_to_partner: v })}
              testid={`fs-${index}-wire-paid-partner`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Deal Totals panel — always derived from funding_sources[] ──
const DealTotalsPanel = ({ totals }) => {
  if (!totals) return null;
  const items = [
    { label: 'Total Invoiced',          value: totals.total_invoiced,          testid: 'deal-totals-invoiced' },
    { label: 'Total Payments',          value: totals.total_payments,          testid: 'deal-totals-payments' },
    { label: 'Total Deposits',          value: totals.total_deposits,          testid: 'deal-totals-deposits' },
    { label: 'Total Card Processing',   value: totals.total_card_processing_fees, testid: 'deal-totals-card-fees' },
    { label: 'Total Convenience Fees',  value: totals.total_convenience_fees,  testid: 'deal-totals-convenience' },
    { label: 'Total Wire Fees',         value: totals.total_wire_fees,         testid: 'deal-totals-wire-fees' },
    { label: 'Total Paid to PTC',       value: totals.total_paid_to_ptc,       testid: 'deal-totals-paid-ptc' },
    { label: 'Total Paid to Partner',   value: totals.total_paid_to_partner,   testid: 'deal-totals-paid-partner' },
    { label: 'Remaining Balance',       value: totals.remaining_balance,       testid: 'deal-totals-remaining', accent: true },
  ];
  return (
    <div
      className="mt-3 rounded-md border-2 border-emerald-300 bg-emerald-50/40 p-3"
      data-testid="funding-sources-deal-totals"
    >
      <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-800 mb-2">
        Deal Totals <span className="text-[9px] text-emerald-600 normal-case font-normal">(from funding_sources[])</span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
        {items.map((it) => (
          <div key={it.label} className="flex flex-col">
            <span className="text-[9px] uppercase text-emerald-700">{it.label}</span>
            <span
              className={`tabular-nums text-xs font-semibold ${
                it.accent ? 'text-emerald-900' : 'text-slate-800'
              }`}
              data-testid={it.testid}
            >
              {fmt(it.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main block ──
const FundingSourcesBlock = ({ clientId, canEdit = true }) => {
  const [sources, setSources]   = useState([]);
  const [totals,  setTotals]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [adding,  setAdding]    = useState(false);

  const refetch = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/funding-sources`);
      setSources(data.funding_sources || []);
      setTotals(data.totals || null);
    } catch (err) {
      toast.error(`Failed to load funding sources: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refetch(); }, [refetch]);

  const patchSource = useCallback(async (sourceId, patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/clients/${clientId}/funding-sources/${sourceId}`,
        patch,
      );
      setSources(data.funding_sources || []);
      setTotals(data.totals || null);
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  }, [clientId]);

  const addSource = useCallback(async () => {
    if (adding) return;
    setAdding(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/funding-sources`,
        { method: METHOD_ACH },
      );
      setSources(data.funding_sources || []);
      setTotals(data.totals || null);
      toast.success('Funding source added');
    } catch (err) {
      toast.error(`Add failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setAdding(false);
    }
  }, [clientId, adding]);

  const removeSource = useCallback(async (sourceId) => {
    if (!window.confirm('Remove this funding source? The audit history is preserved.')) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/clients/${clientId}/funding-sources/${sourceId}`);
      setSources(data.funding_sources || []);
      setTotals(data.totals || null);
      toast.success('Funding source removed');
    } catch (err) {
      toast.error(`Remove failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  }, [clientId]);

  const moveSource = useCallback(async (sourceId, toIndex) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/funding-sources/${sourceId}/reorder`,
        { to: toIndex },
      );
      setSources(data.funding_sources || []);
      setTotals(data.totals || null);
    } catch (err) {
      toast.error(`Reorder failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  }, [clientId]);

  const cardHandlers = useMemo(() => sources.map((src, idx) => ({
    onPatch:  (patch) => patchSource(src.id, patch),
    onRemove: () => removeSource(src.id),
    onMoveUp:   () => moveSource(src.id, idx - 1),
    onMoveDown: () => moveSource(src.id, idx + 1),
  })), [sources, patchSource, removeSource, moveSource]);

  if (loading) {
    return (
      <Card className="rounded-none shadow-none border-b" data-testid="dashboard-block-funding-sources">
        <CardHeader className="pb-1 pt-2 px-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
            <CreditCard className="h-4 w-4 text-slate-500" />
            Funding Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3 pt-0">
          <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading funding sources…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-none shadow-none border-b" data-testid="dashboard-block-funding-sources">
      <CardHeader className="pb-1 pt-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
            <CreditCard className="h-4 w-4 text-slate-500" />
            Funding Sources
            <span
              className="text-[10px] text-slate-400 normal-case font-normal"
              data-testid="funding-sources-count"
            >
              ({sources.length})
            </span>
          </CardTitle>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={addSource}
              disabled={adding || saving}
              className="h-7 text-xs"
              data-testid="funding-sources-add-btn"
            >
              {adding ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
              Add Funding Source
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-0">
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="funding-sources-grid"
        >
          {sources.map((src, idx) => (
            <FundingSourceCard
              key={src.id}
              source={src}
              index={idx}
              disabled={!canEdit}
              saving={saving}
              canMoveUp={idx > 0}
              canMoveDown={idx < sources.length - 1}
              {...cardHandlers[idx]}
            />
          ))}
        </div>

        <DealTotalsPanel totals={totals} />
      </CardContent>
    </Card>
  );
};

export default FundingSourcesBlock;
